// Copyright (c) 2026 Nathan Blackman / Find My Trout. All rights reserved.

const WV_STREAMS_API = 'https://services.wvgis.wvu.edu/arcgis/rest/services/Applications/dnrRec_fishing/MapServer/4/query?where=1%3D1&outFields=*&outSR=4326&f=geojson';
const WV_COUNTIES_API = 'https://services.wvgis.wvu.edu/arcgis/rest/services/Boundaries/wv_political_boundary/MapServer/0/query?where=1%3D1&outFields=*&outSR=4326&f=geojson';

// Map trout type abbreviations to full names
export const getTroutTypeFullName = (abbreviation) => {
  if (!abbreviation) return 'Unknown';
  
  const troutTypes = {
    'B': 'Brook Trout',
    'BR': 'Brown Trout',
    'R': 'Rainbow Trout',
    'RR': 'Rainbow Reproduction',
    'BW': 'Brook/Wild',
    'RW': 'Rainbow/Wild',
    'BRW': 'Brown/Wild',
    'M': 'Mixed Species',
    'MJ': 'Mixed (Brook & Brown)',
    'NS': 'Non-Stocked',
    'F': 'Fingerling Stocked',
    'CR': 'Catch & Release',
    'W-F': 'Wild/Fingerling Stocked',
    'BW-F': 'Brook/Wild Fingerling',
    'BW-Q': 'Brook/Wild Quality',
    'MD': 'Mixed Delayed Harvest',
    'Q': 'Quality Stocked',
    'Wild': 'Wild Trout',
    'Stocked': 'Stocked Trout',
    'Native': 'Native Brook Trout',
    'Heritage': 'Heritage Strain',
    'Trophy': 'Trophy Trout',
    'Delayed Harvest': 'Delayed Harvest',
    'DH': 'Delayed Harvest',
    'C&R': 'Catch & Release Only',
    'W': 'Wild',
    'S': 'Stocked',
    'N': 'Native'
  };
  
  // Check for exact match first
  if (troutTypes[abbreviation]) {
    return troutTypes[abbreviation];
  }
  
  // Check for case-insensitive match
  const upperAbbr = abbreviation.toUpperCase();
  if (troutTypes[upperAbbr]) {
    return troutTypes[upperAbbr];
  }
  
  // If no match found, return the original (might already be full name)
  return abbreviation;
};

export const fetchWVStreams = async () => {
  try {
    const response = await fetch(WV_STREAMS_API);
    
    if (!response.ok) {
      throw new Error('Failed to fetch stream data');
    }
    
    const data = await response.json();
    
    // Transform GeoJSON features to our stream format
    const streams = data.features.map((feature, index) => {
      const props = feature.properties;
      const coords = feature.geometry.coordinates;
      
      // For LineString, get the first point
      let lat, lng;
      if (feature.geometry.type === 'LineString' && coords.length > 0) {
        lng = coords[0][0];
        lat = coords[0][1];
      } else if (feature.geometry.type === 'MultiLineString' && coords.length > 0 && coords[0].length > 0) {
        lng = coords[0][0][0];
        lat = coords[0][0][1];
      }
      
      // Get stock code and convert to full name
      const stockCodeRaw = props.StockCode || props.TROUT_TYPE || '';
      const stockCode = getTroutTypeFullName(stockCodeRaw);
      
      return {
        id: props.OBJECTID || index,
        name: props.Name || props.WATER_NAME || 'Unknown Stream',
        county: props.County_1 || props.COUNTY || '',
        county2: props.County_2 || null,
        county3: props.County_3 || null,
        mileage: props.Mileage || props.MILES || 0,
        stockCode: stockCode,
        stockCodeRaw: stockCodeRaw, // Keep original for debugging if needed
        regType: props.RegType || props.REG || 'General',
        nearCity: props.NearCity || props.NEAR_CITY || '',
        hatchery: props.Hatchery || '',
        stockExtent: props.StockExtent || '',
        coordinates: lat && lng ? { lat, lng } : null,
        geometry: feature.geometry // Keep original geometry for line rendering
      };
    }).filter(stream => stream.coordinates); // Only include streams with valid coordinates
    
    return streams;
  } catch (error) {
    console.error('Error fetching WV streams:', error);
    return [];
  }
};

export const fetchWVCounties = async () => {
  try {
    const response = await fetch(WV_COUNTIES_API);
    
    if (!response.ok) {
      throw new Error('Failed to fetch county data');
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching WV counties:', error);
    return null;
  }
};